import { useState, useEffect, useCallback } from 'react';
import { apiFetch } from '../../fetchInterceptor';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Skeleton } from '../ui/skeleton';
import { Tooltip, TooltipTrigger, TooltipContent } from '../ui/tooltip';
import {
  Plus, Loader2, Trash2, Edit3, DollarSign, Wallet,
  Landmark, GraduationCap, Home, Heart, CircleDollarSign,
  Briefcase, TrendingUp, Info, X, Check
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { fmt, API_URL } from './constants';

// Reusable tooltip wrapper
const Tip = ({ children, text, side = 'top' }) => (
  <Tooltip>
    <TooltipTrigger asChild>{children}</TooltipTrigger>
    <TooltipContent side={side} className="max-w-xs text-xs leading-relaxed">{text}</TooltipContent>
  </Tooltip>
);

const INCOME_CATEGORIES = [
  { id: 'w2', label: 'W-2 Wages', icon: Briefcase, color: 'text-blue-600 dark:text-blue-400', bg: 'bg-blue-100 dark:bg-blue-900/30' },
  { id: 'retirement', label: 'Retirement', icon: GraduationCap, color: 'text-amber-600 dark:text-amber-400', bg: 'bg-amber-100 dark:bg-amber-900/30' },
  { id: 'investment', label: 'Investment', icon: TrendingUp, color: 'text-emerald-600 dark:text-emerald-400', bg: 'bg-emerald-100 dark:bg-emerald-900/30' },
  { id: 'rental', label: 'Rental', icon: Home, color: 'text-violet-600 dark:text-violet-400', bg: 'bg-violet-100 dark:bg-violet-900/30' },
  { id: 'spouse', label: 'Spouse', icon: Heart, color: 'text-pink-600 dark:text-pink-400', bg: 'bg-pink-100 dark:bg-pink-900/30' },
  { id: 'other', label: 'Other', icon: CircleDollarSign, color: 'text-muted-foreground', bg: 'bg-muted' },
];

const FILING_STATUSES = [
  { id: 'single', label: 'Single' },
  { id: 'married_jointly', label: 'Married Filing Jointly' },
  { id: 'married_separately', label: 'Married Filing Separately' },
  { id: 'head_of_household', label: 'Head of Household' },
];

const RETIREMENT_TYPES = [
  { id: 'social_security', label: 'Social Security' },
  { id: 'pension', label: 'Pension' },
  { id: 'ira', label: 'IRA Distribution' },
  { id: '401k', label: '401(k) Distribution' },
];

const INVESTMENT_TYPES = [
  { id: 'dividends', label: 'Dividends' },
  { id: 'capital_gains_long', label: 'Long-Term Capital Gains' },
  { id: 'capital_gains_short', label: 'Short-Term Capital Gains' },
  { id: 'interest', label: 'Interest Income' },
];

function IncomeProfileSection({ workspaceId, businessIncome, onIncomeChange }) {
  const [entries, setEntries] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [formCat, setFormCat] = useState('w2');
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [filingStatus, setFilingStatus] = useState('single');
  const [stateCode, setStateCode] = useState('');
  const [statesList, setStatesList] = useState([]);
  const currentYear = new Date().getFullYear();

  const fetchData = useCallback(async () => {
    if (!workspaceId) return;
    setLoading(true);
    try {
      const [entriesRes, summaryRes] = await Promise.all([
        fetch(`${API_URL}/api/workspaces/${workspaceId}/other-income?year=${currentYear}`),
        fetch(`${API_URL}/api/workspaces/${workspaceId}/other-income/summary?year=${currentYear}`),
      ]);
      if (entriesRes.ok) { const d = await entriesRes.json(); setEntries(d.entries || []); }
      if (summaryRes.ok) { const d = await summaryRes.json(); setSummary(d); setFilingStatus(d.filing_status || 'single'); setStateCode(d.state || ''); }
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [workspaceId, currentYear]);

  useEffect(() => { fetchData(); }, [fetchData]);

  useEffect(() => {
    fetch(`${API_URL}/api/tax-settings/states`)
      .then(r => r.ok ? r.json() : []).then(d => { setStatesList(d); sessionStorage.setItem('statesList', JSON.stringify(d)); }).catch(() => {});
  }, []);

  const openAddForm = (cat) => {
    setFormCat(cat);
    setEditingEntry(null);
    setForm({ year: currentYear });
    setShowForm(true);
  };

  const openEditForm = (entry) => {
    setFormCat(entry.category);
    setEditingEntry(entry);
    setForm({ ...entry });
    setShowForm(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = { ...form, category: formCat, year: currentYear, label: form.label || getAutoLabel() };
      if (editingEntry) {
        await apiFetch(`${API_URL}/api/workspaces/${workspaceId}/other-income/${editingEntry.income_id}`, {
          method: 'PUT', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
      } else {
        await apiFetch(`${API_URL}/api/workspaces/${workspaceId}/other-income`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
      }
      setShowForm(false);
      setForm({});
      fetchData();
      onIncomeChange?.();
    } catch (e) { console.error(e); }
    finally { setSaving(false); }
  };

  const handleDelete = async (incomeId) => {
    await apiFetch(`${API_URL}/api/workspaces/${workspaceId}/other-income/${incomeId}`, { method: 'DELETE' });
    fetchData();
    onIncomeChange?.();
  };

  const handleFilingStatusChange = async (status) => {
    setFilingStatus(status);
    await apiFetch(`${API_URL}/api/workspaces/${workspaceId}/tax-settings/filing-status`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ filing_status: status, tax_year: currentYear, state: stateCode }),
    });
    fetchData();
    onIncomeChange?.();
  };

  const handleStateChange = async (code) => {
    setStateCode(code);
    await apiFetch(`${API_URL}/api/workspaces/${workspaceId}/tax-settings/filing-status`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ filing_status: filingStatus, tax_year: currentYear, state: code }),
    });
    fetchData();
    onIncomeChange?.();
  };

  const getAutoLabel = () => {
    if (formCat === 'w2') return form.employer_name || 'W-2 Income';
    if (formCat === 'retirement') return form.source_name || 'Retirement Income';
    if (formCat === 'investment') return INVESTMENT_TYPES.find(t => t.id === form.investment_type)?.label || 'Investment Income';
    if (formCat === 'rental') return form.property_name || 'Rental Income';
    if (formCat === 'spouse') return `${form.spouse_name || 'Spouse'} Income`;
    return form.description || 'Other Income';
  };

  const getEntryAmount = (e) => {
    if (e.category === 'w2') return e.gross_wages || 0;
    if (e.category === 'retirement') return e.annual_amount || 0;
    if (e.category === 'investment') return e.investment_amount || 0;
    if (e.category === 'rental') return (e.gross_rent || 0) - (e.rental_expenses || 0);
    if (e.category === 'spouse') return e.spouse_amount || 0;
    return e.amount || 0;
  };

  const otherTotal = summary?.grand_total || 0;
  const bizIncome = businessIncome || 0;
  const combinedTotal = bizIncome + otherTotal;

  const renderForm = () => {
    const set = (field, val) => setForm(prev => ({ ...prev, [field]: val }));
    const numInput = (field, label, placeholder) => (
      <div key={field}>
        <label className="text-[11px] font-medium text-muted-foreground mb-1 block">{label}</label>
        <Input type="number" value={form[field] || ''} onChange={(e) => set(field, parseFloat(e.target.value) || 0)}
          placeholder={placeholder} className="h-9 text-sm" data-testid={`income-${field}`} />
      </div>
    );
    const textInput = (field, label, placeholder) => (
      <div key={field}>
        <label className="text-[11px] font-medium text-muted-foreground mb-1 block">{label}</label>
        <Input value={form[field] || ''} onChange={(e) => set(field, e.target.value)}
          placeholder={placeholder} className="h-9 text-sm" data-testid={`income-${field}`} />
      </div>
    );

    return (
      <div className="space-y-3">
        {formCat === 'w2' && (<>
          {textInput('employer_name', 'Employer Name', 'Acme Corporation')}
          {numInput('gross_wages', 'Gross Wages (Annual)', '$85,000')}
          <div className="grid grid-cols-2 gap-3">
            {numInput('federal_withholding', 'Federal Withholding', '$12,000')}
            {numInput('state_withholding', 'State Withholding', '$4,000')}
          </div>
        </>)}
        {formCat === 'retirement' && (<>
          {textInput('source_name', 'Source', 'Fidelity, Social Security Admin')}
          <div>
            <label className="text-[11px] font-medium text-muted-foreground mb-1 block">Type</label>
            <div className="flex flex-wrap gap-1.5">
              {RETIREMENT_TYPES.map(t => (
                <button key={t.id} onClick={() => set('retirement_type', t.id)}
                  className={cn("px-2.5 py-1 rounded-full text-[11px] font-medium border transition-all", form.retirement_type === t.id ? "bg-amber-100 border-amber-300 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400" : "border-border text-muted-foreground")}>{t.label}</button>
              ))}
            </div>
          </div>
          {numInput('annual_amount', 'Annual Amount', '$24,000')}
          {numInput('tax_withholding', 'Tax Withholding', '$3,600')}
        </>)}
        {formCat === 'investment' && (<>
          <div>
            <label className="text-[11px] font-medium text-muted-foreground mb-1 block">Type</label>
            <div className="flex flex-wrap gap-1.5">
              {INVESTMENT_TYPES.map(t => (
                <button key={t.id} onClick={() => set('investment_type', t.id)}
                  className={cn("px-2.5 py-1 rounded-full text-[11px] font-medium border transition-all", form.investment_type === t.id ? "bg-emerald-100 border-emerald-300 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400" : "border-border text-muted-foreground")}>{t.label}</button>
              ))}
            </div>
          </div>
          {numInput('investment_amount', 'Amount', '$15,000')}
        </>)}
        {formCat === 'rental' && (<>
          {textInput('property_name', 'Property Name/Address', '123 Main St')}
          <div className="grid grid-cols-2 gap-3">
            {numInput('gross_rent', 'Gross Annual Rent', '$36,000')}
            {numInput('rental_expenses', 'Annual Expenses', '$18,000')}
          </div>
        </>)}
        {formCat === 'spouse' && (<>
          {textInput('spouse_name', 'Spouse Name', 'Jane')}
          {textInput('spouse_income_type', 'Income Type', 'W-2, Self-Employment, etc.')}
          <div className="grid grid-cols-2 gap-3">
            {numInput('spouse_amount', 'Gross Annual Income', '$65,000')}
            {numInput('spouse_withholding', 'Total Withholding', '$10,000')}
          </div>
        </>)}
        {formCat === 'other' && (<>
          {textInput('description', 'Description', 'Alimony, Awards, etc.')}
          {numInput('amount', 'Annual Amount', '$5,000')}
        </>)}
        {textInput('label', 'Label (optional)', getAutoLabel())}

        {/* State Allocation */}
        <div className="pt-2 border-t border-border">
          <label className="text-[11px] font-medium text-muted-foreground mb-2 block">State Allocation</label>
          {(() => {
            const allocs = form.state_allocations?.length > 0 ? form.state_allocations : [{ state: stateCode || '', percentage: 100 }];
            const updateAllocs = (newAllocs) => set('state_allocations', newAllocs);
            const setAlloc = (idx, field, val) => {
              const next = [...allocs];
              next[idx] = { ...next[idx], [field]: val };
              updateAllocs(next);
            };
            const addState = () => updateAllocs([...allocs, { state: '', percentage: 0 }]);
            const removeAlloc = (idx) => {
              if (allocs.length <= 1) return;
              const next = allocs.filter((_, i) => i !== idx);
              if (next.length === 1) next[0].percentage = 100;
              updateAllocs(next);
            };
            const totalPct = allocs.reduce((s, a) => s + (a.percentage || 0), 0);
            return (
              <div className="space-y-2">
                {allocs.map((a, idx) => (
                  <div key={idx} className="flex items-center gap-2">
                    <select value={a.state} onChange={(e) => setAlloc(idx, 'state', e.target.value)}
                      className="h-8 px-2 text-xs rounded-md border border-border bg-card text-foreground flex-1 outline-none"
                      data-testid={`state-alloc-${idx}`}>
                      <option value="">Select state...</option>
                      {statesList.map(s => <option key={s.code} value={s.code}>{s.name}</option>)}
                    </select>
                    <div className="flex items-center gap-1">
                      <Input type="number" min="0" max="100" value={a.percentage || ''} onChange={(e) => setAlloc(idx, 'percentage', parseFloat(e.target.value) || 0)}
                        className="h-8 w-16 text-xs text-center" data-testid={`state-pct-${idx}`} />
                      <span className="text-[10px] text-muted-foreground">%</span>
                    </div>
                    {allocs.length > 1 && (
                      <button onClick={() => removeAlloc(idx)} className="text-muted-foreground hover:text-rose-500 transition-colors" data-testid={`remove-state-${idx}`}>
                        <X className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                ))}
                <div className="flex items-center justify-between">
                  <button onClick={addState} className="text-[10px] text-violet-600 dark:text-violet-400 hover:underline flex items-center gap-1" data-testid="add-state-alloc">
                    <Plus className="w-3 h-3" />Split to another state
                  </button>
                  {totalPct !== 100 && allocs.some(a => a.state) && (
                    <span className="text-[10px] text-rose-500 font-medium">Total: {totalPct}% (must equal 100%)</span>
                  )}
                </div>
              </div>
            );
          })()}
        </div>
      </div>
    );
  };

  return (
    <Card className="border-border shadow-sm" data-testid="income-profile-section">
      <CardContent className="p-6">
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-2">
            <Wallet className="w-4 h-4 text-muted-foreground" />
            <span className="text-xs font-semibold uppercase tracking-[0.15em] text-muted-foreground">Full Income Profile</span>
            <Tip text="Add non-business income (W-2s, retirement, investments, spouse) to give CFO Buck your complete tax picture for accurate planning.">
              <span><Info className="w-3.5 h-3.5 text-muted-foreground/50 cursor-help" /></span>
            </Tip>
          </div>
          <span className="text-[10px] text-muted-foreground">Tax Year {currentYear}</span>
        </div>

        {/* Filing Status */}
        <div className="mb-5">
          <label className="text-[10px] font-semibold uppercase tracking-[0.15em] text-muted-foreground mb-2 block">Filing Status</label>
          <div className="flex flex-wrap gap-1.5" data-testid="filing-status-selector">
            {FILING_STATUSES.map(fs => (
              <button key={fs.id} onClick={() => handleFilingStatusChange(fs.id)}
                className={cn("px-3 py-1.5 rounded-full text-[11px] font-medium border transition-all",
                  filingStatus === fs.id ? "bg-emerald-50 dark:bg-emerald-900/20 border-emerald-300 dark:border-emerald-700 text-emerald-700 dark:text-emerald-400" : "border-border text-muted-foreground"
                )} data-testid={`filing-${fs.id}`}>{fs.label}</button>
            ))}
          </div>
        </div>

        {/* State Selection */}
        <div className="mb-5">
          <label className="text-[10px] font-semibold uppercase tracking-[0.15em] text-muted-foreground mb-2 block">State of Residence</label>
          <div className="flex items-center gap-3">
            <select
              value={stateCode}
              onChange={(e) => handleStateChange(e.target.value)}
              className="h-9 px-3 text-sm rounded-lg border border-border bg-card text-foreground focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-400 outline-none transition-all w-64"
              data-testid="state-selector"
            >
              <option value="">Select a state...</option>
              {statesList.map(s => (
                <option key={s.code} value={s.code}>{s.name} ({s.rate > 0 ? `~${s.rate}%` : 'No income tax'})</option>
              ))}
            </select>
            {stateCode && (() => {
              const st = statesList.find(s => s.code === stateCode);
              return st ? (
                <div className="flex items-center gap-2">
                  <span className={cn("text-xs font-medium px-2 py-0.5 rounded-full", st.rate > 0 ? "bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400" : "bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-400")}>
                    {st.rate > 0 ? `~${st.rate}% effective rate` : 'No state income tax'}
                  </span>
                  {st.ptet && <Tip text="This state offers a Pass-Through Entity Tax (PTET) election, which can bypass the $10K SALT deduction cap for business owners."><span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-violet-50 dark:bg-violet-900/20 text-violet-600 dark:text-violet-400 cursor-help">PTET Available</span></Tip>}
                </div>
              ) : null;
            })()}
          </div>
        </div>

        {/* Income Summary Bar */}
        <div className="rounded-xl bg-muted border border-border p-4 mb-5">
          <div className="grid grid-cols-3 gap-4 text-center">
            <Tip text="Net income from your business books (YTD revenue minus expenses).">
              <div className="cursor-help">
                <p className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground mb-0.5">Business Income</p>
                <p className="text-lg font-bold text-foreground" style={{ fontFamily: "'Outfit', sans-serif" }}>{fmt(bizIncome)}</p>
              </div>
            </Tip>
            <Tip text="Total non-business income you've entered (W-2s, retirement, investments, spouse, etc.).">
              <div className="cursor-help">
                <p className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground mb-0.5">Other Income</p>
                <p className="text-lg font-bold text-violet-600 dark:text-violet-400" style={{ fontFamily: "'Outfit', sans-serif" }}>{fmt(otherTotal)}</p>
              </div>
            </Tip>
            <Tip text="Combined gross income used for tax bracket calculations and marginal rate estimation.">
              <div className="cursor-help">
                <p className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground mb-0.5">Combined Total</p>
                <p className="text-lg font-bold text-emerald-600 dark:text-emerald-400" style={{ fontFamily: "'Outfit', sans-serif" }}>{fmt(combinedTotal)}</p>
              </div>
            </Tip>
          </div>
          {summary?.total_withholding > 0 && (
            <div className="mt-3 pt-3 border-t border-border/50 text-center">
              <Tip text="Total federal and state tax already withheld from your non-business income this year.">
                <span className="text-[11px] text-muted-foreground cursor-help"><Landmark className="w-3 h-3 inline mr-1" />Tax Already Withheld: <strong className="text-muted-foreground">{fmt(summary.total_withholding)}</strong></span>
              </Tip>
            </div>
          )}
        </div>

        {/* Add Income Categories */}
        <div className="flex flex-wrap gap-2 mb-4" data-testid="add-income-categories">
          {INCOME_CATEGORIES.map(cat => {
            const CatIcon = cat.icon;
            const count = entries.filter(e => e.category === cat.id).length;
            return (
              <button key={cat.id} onClick={() => openAddForm(cat.id)}
                className={cn("flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-medium border border-border text-muted-foreground hover:border-primary/30 hover:text-foreground transition-all")}
                data-testid={`add-income-${cat.id}`}>
                <Plus className="w-3 h-3" /><CatIcon className="w-3 h-3" />{cat.label}{count > 0 && <span className="text-[9px] bg-muted px-1.5 rounded-full">{count}</span>}
              </button>
            );
          })}
        </div>

        {/* Entry List */}
        {loading ? (
          <div className="space-y-2">{[1,2].map(i => <Skeleton key={i} className="h-14 rounded-lg" />)}</div>
        ) : entries.length > 0 ? (
          <div className="space-y-2" data-testid="income-entries">
            {entries.map(entry => {
              const cat = INCOME_CATEGORIES.find(c => c.id === entry.category) || INCOME_CATEGORIES[5];
              const CatIcon = cat.icon;
              const amt = getEntryAmount(entry);
              const descriptor = entry.category === 'w2' ? entry.employer_name
                : entry.category === 'retirement' ? entry.source_name
                : entry.category === 'investment' ? (INVESTMENT_TYPES.find(t => t.id === entry.investment_type)?.label || null)
                : entry.category === 'rental' ? entry.property_name
                : entry.category === 'spouse' ? entry.spouse_name
                : entry.description;
              return (
                <div key={entry.income_id} className="flex items-center gap-3 p-3 rounded-lg bg-muted/20 border border-border group" data-testid={`income-entry-${entry.income_id}`}>
                  <div className={cn("w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0", cat.bg)}>
                    <CatIcon className={cn("w-4 h-4", cat.color)} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-foreground truncate">{entry.label}</p>
                    <p className="text-[10px] text-muted-foreground truncate">
                      {cat.label}{descriptor ? <> &middot; <span className="text-muted-foreground">{descriptor}</span></> : null} &middot; {entry.year}
                      {entry.state_allocations?.length > 0 && entry.state_allocations.some(a => a.state) && (
                        <> &middot; {entry.state_allocations.filter(a => a.state).map(a => entry.state_allocations.length > 1 ? `${a.state} ${a.percentage}%` : a.state).join(', ')}</>
                      )}
                    </p>
                  </div>
                  <p className={cn("text-sm font-bold", amt >= 0 ? "text-emerald-600 dark:text-emerald-400" : "text-rose-600 dark:text-rose-400")}>{fmt(amt)}</p>
                  <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                    <Tip text="Edit"><span>
                      <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => openEditForm(entry)}><Edit3 className="w-3 h-3" /></Button>
                    </span></Tip>
                    <Tip text="Delete"><span>
                      <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-rose-500" onClick={() => handleDelete(entry.income_id)}><Trash2 className="w-3 h-3" /></Button>
                    </span></Tip>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-6">
            <Wallet className="w-8 h-8 mx-auto text-muted-foreground/50 mb-2" />
            <p className="text-xs text-muted-foreground">No other income added yet. Add W-2s, retirement, or investment income above to complete your tax picture.</p>
          </div>
        )}

        {/* Add/Edit Form */}
        {showForm && (
          <div className="mt-4 p-4 rounded-xl border border-violet-200 dark:border-violet-800 bg-violet-50/30 dark:bg-violet-900/10" data-testid="income-form">
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-sm font-semibold text-foreground">
                {editingEntry ? 'Edit' : 'Add'} {INCOME_CATEGORIES.find(c => c.id === formCat)?.label || 'Income'}
              </h4>
              <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => setShowForm(false)}><X className="w-3.5 h-3.5" /></Button>
            </div>
            {/* Category pills if adding new */}
            {!editingEntry && (
              <div className="flex flex-wrap gap-1.5 mb-3">
                {INCOME_CATEGORIES.map(cat => (
                  <button key={cat.id} onClick={() => { setFormCat(cat.id); setForm({ year: currentYear }); }}
                    className={cn("px-2.5 py-1 rounded-full text-[10px] font-medium border transition-all",
                      formCat === cat.id ? "bg-violet-100 border-violet-300 text-violet-700 dark:bg-violet-900/30 dark:text-violet-400" : "border-border text-muted-foreground"
                    )}>{cat.label}</button>
                ))}
              </div>
            )}
            {renderForm()}
            <div className="flex justify-end gap-2 mt-4">
              <Button variant="outline" size="sm" onClick={() => setShowForm(false)}>Cancel</Button>
              <Button size="sm" onClick={handleSave} disabled={saving}
                className="bg-gradient-to-r from-violet-500 to-indigo-600 text-white" data-testid="save-income-btn">
                {saving ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Check className="w-3 h-3 mr-1" />}{editingEntry ? 'Update' : 'Add Income'}
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}



// ─── TAX MAPPER TAB ──────────────────────────────────────────────────────────
export { IncomeProfileSection };
